import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import {
  Autocomplete,
  Box,
  Popper,
  Stack,
  Typography,
  type AutocompleteProps,
  type FilterOptionsState,
  type PopperProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import type { IkpuInfo } from '@/api/domains/search.types';
import { isNumeric } from '@/shared/helpers/string.helpers';
import { useIkpuInfo, useIkpusInfo } from '@/shared/hooks/useIkpusInfo';

import { CustomListBox } from './CustomListBox';

export type IkpuFieldProps = Omit<
  AutocompleteProps<IkpuOption, false, false, false>,
  'freeSolo' | 'autoComplete' | 'includeInputInList' | 'filterSelectedOptions' | 'loading' | 'noOptionsText' | 'options'
>;

const WiderPopper = styled((props: PopperProps) => <Popper {...props} />)(({ theme }) => ({
  width: '600px !important',
  zIndex: theme.zIndex.modal,
}));

const StyledOption = styled('li', {
  shouldForwardProp: prop => prop !== 'isLast',
})<{ isLast?: boolean }>(({ theme, isLast }) => ({
  borderBottom: isLast ? 'none' : theme.shape.divider,
  borderRadius: '0 !important',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

type IkpuOption = IkpuInfo & { __isSearch?: true };

const getFilteredOptions = (options: IkpuOption[], params: FilterOptionsState<IkpuOption>) => {
  const { inputValue } = params;
  const normalizedInput = inputValue.trim().toLowerCase();

  const filtered = options.filter(
    option =>
      option.ikpu.toLowerCase().includes(normalizedInput) || option.drug_name.toLowerCase().includes(normalizedInput),
  );

  const isExisting = options.some(option => inputValue === option.ikpu);

  if (inputValue && isNumeric(inputValue) && !isExisting) {
    const commonMockData = {
      barcode: '',
      drug_name: '',
      maker_name: '',
      has_marks: false,
      package_code: 0,
      package_name: '',
    };

    filtered.push({
      ...commonMockData,
      ikpu: inputValue,
      __isSearch: true,
    });
  }

  return filtered;
};

export const IkpuField = (props: IkpuFieldProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [customIkpu, setCustomIkpu] = useState('');

  const { setValue, setError } = useFormContext();

  const renderIkpuOption = (props: React.HTMLAttributes<HTMLLIElement>, option: IkpuOption) => (
    <StyledOption {...props} isLast={option.__isSearch}>
      {option.__isSearch ? (
        <Box py={1} px={1.5}>
          <Typography variant="Body/medium">{t('nomenclature-page:form-controls.ikpu-field.add')}</Typography>{' '}
          <Typography variant="Body/Bold/medium">{option.ikpu}</Typography>
        </Box>
      ) : (
        <Stack spacing={2} py={1} px={1.5}>
          <Box>
            <Typography variant="Body/Bold/medium">{option.drug_name}</Typography>
            <Typography variant="Body/small" color="neutral.700" component="p">
              {option.maker_name}
            </Typography>
          </Box>
          <Box>
            <Typography variant="Body/medium">
              {t('nomenclature-page:modals.form.ikpu-code')}: {option.ikpu}
            </Typography>
            <Typography variant="Body/small" color="neutral.700" component="p">
              {t('nomenclature-page:modals.form.barcode')}: {option.barcode}
            </Typography>
          </Box>
        </Stack>
      )}
    </StyledOption>
  );

  useDebounce(() => setSearchQuery(inputValue), 500, [inputValue]);

  const { isFetching, ikpusInfo } = useIkpusInfo(
    {
      query: searchQuery,
    },
    {
      enabled: searchQuery.length > 2,
    },
  );

  const { isFetching: isFetchingCustomIkpu, ikpuInfo } = useIkpuInfo(
    {
      ikpu: customIkpu,
    },
    {
      enabled: !!customIkpu,
    },
  );

  const handleInputChange = (_event: React.SyntheticEvent, inputValue: string) => {
    setInputValue(inputValue);
  };

  useEffect(() => {
    if (customIkpu && !isFetchingCustomIkpu) {
      if (ikpuInfo) {
        setValue('has_marks', ikpuInfo.has_marks, { shouldDirty: true });
        setValue('package_code', `${ikpuInfo.package_code} - ${ikpuInfo.package_name}`, { shouldDirty: true });
      } else {
        setError('ikpu', { message: t('nomenclature-page:form-controls.ikpu-field.no-data-error') });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customIkpu, isFetchingCustomIkpu, ikpuInfo]);

  const handleChange = (_: unknown, value: IkpuOption | null) => {
    if (!value) {
      setValue('ikpu', '', { shouldValidate: true, shouldDirty: true });
      setValue('has_marks', false, { shouldDirty: true });
      setValue('package_code', '', { shouldDirty: true });
      setCustomIkpu('');
      return;
    }

    setValue('ikpu', value.ikpu, { shouldValidate: true, shouldDirty: true });

    if (value.__isSearch && value.ikpu.length === 17) {
      setCustomIkpu(value.ikpu);
    } else {
      setValue('has_marks', value.has_marks, { shouldDirty: true });
      if (value.package_code)
        setValue('package_code', `${value.package_code} - ${value.package_name}`, { shouldDirty: true });
    }
  };

  return (
    <Autocomplete
      {...props}
      onChange={handleChange}
      autoComplete
      includeInputInList
      filterSelectedOptions
      options={ikpusInfo}
      filterOptions={getFilteredOptions}
      handleHomeEndKeys
      PopperComponent={WiderPopper}
      ListboxComponent={props => <CustomListBox {...props} loading={isFetching} searchQuery={searchQuery} />}
      getOptionLabel={option => (typeof option === 'string' ? option : option.ikpu)}
      isOptionEqualToValue={(option, value) => option.ikpu === value.ikpu}
      onInputChange={handleInputChange}
      renderOption={renderIkpuOption}
    />
  );
};
